import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, TrendingUp, Users, BarChart } from 'lucide-react';
import { useEnhancedMobile } from '@/hooks/use-enhanced-mobile';

interface KPICardsProps {
  timeRange?: string;
  summary: {
    totalValue: number;
    totalInitialValue: number;
    totalROI: number;
    activeHoldings: number;
    totalHoldings: number;
  };
  loading: boolean;
}

export const KPICards: React.FC<KPICardsProps> = ({ timeRange = '12M', summary, loading }) => {
  const { isMobile, isTablet } = useEnhancedMobile();
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  return (
    <div className={`grid gap-3 sm:gap-4 md:gap-6 ${
      isMobile ? 'grid-cols-2' : isTablet ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
    }`}>
      <Card>
        <CardHeader className={`flex flex-row items-center justify-between space-y-0 ${isMobile ? 'pb-1' : 'pb-2'}`}>
          <CardTitle className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>Total Holdings</CardTitle>
          <div className={`${isMobile ? 'p-1.5' : 'p-2'} rounded-lg bg-primary`}>
            <DollarSign className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-primary-foreground`} />
          </div>
        </CardHeader>
        <CardContent className={isMobile ? 'pt-0' : ''}>
          <div className={`font-bold ${isMobile ? 'text-lg' : 'text-2xl'}`}>
            {loading ? '...' : summary.totalHoldings}
          </div>
          <p className={`text-muted-foreground ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
            {summary.activeHoldings} active
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className={`flex flex-row items-center justify-between space-y-0 ${isMobile ? 'pb-1' : 'pb-2'}`}>
          <CardTitle className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>Portfolio</CardTitle>
          <div className={`${isMobile ? 'p-1.5' : 'p-2'} rounded-lg bg-primary`}>
            <TrendingUp className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-primary-foreground`} />
          </div>
        </CardHeader>
        <CardContent className={isMobile ? 'pt-0' : ''}>
          <div className={`font-bold ${isMobile ? 'text-lg' : 'text-2xl'}`}>
            {loading ? '...' : formatCurrency(summary.totalValue)}
          </div>
          <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} ${summary.totalROI >= 0 ? 'text-success' : 'text-destructive'}`}>
            {loading ? '...' : `${formatPercentage(summary.totalROI)} ROI`}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className={`flex flex-row items-center justify-between space-y-0 ${isMobile ? 'pb-1' : 'pb-2'}`}>
          <CardTitle className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>Invested</CardTitle>
          <div className={`${isMobile ? 'p-1.5' : 'p-2'} rounded-lg bg-primary`}>
            <Users className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-primary-foreground`} />
          </div>
        </CardHeader>
        <CardContent className={isMobile ? 'pt-0' : ''}>
          <div className={`font-bold ${isMobile ? 'text-lg' : 'text-2xl'}`}>
            {loading ? '...' : formatCurrency(summary.totalInitialValue)}
          </div>
          <p className={`text-muted-foreground ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
            {isMobile ? 'Capital' : 'Total invested capital'}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className={`flex flex-row items-center justify-between space-y-0 ${isMobile ? 'pb-1' : 'pb-2'}`}>
          <CardTitle className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>P/L</CardTitle>
          <div className={`${isMobile ? 'p-1.5' : 'p-2'} rounded-lg bg-primary`}>
            <BarChart className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-primary-foreground`} />
          </div>
        </CardHeader>
        <CardContent className={isMobile ? 'pt-0' : ''}>
          <div className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold ${
            summary.totalValue - summary.totalInitialValue >= 0 ? 'text-success' : 'text-destructive'
          }`}>
            {loading ? '...' : formatCurrency(summary.totalValue - summary.totalInitialValue)}
          </div>
          <p className={`text-muted-foreground ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
            {isMobile ? 'Net change' : 'Net portfolio change'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};